import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Search, BadgeCheck } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { CREATORS, CREATOR_TAGS } from "../constants/creators";
import { Eyebrow, Reveal } from "../components/Primitives";

const initials = (n) => n.split(" ").map((w) => w[0]).join("");

const CreatorCard = ({ c, i }) => {
  const { t, lang } = useLang();
  return (
    <motion.article
      data-testid={`creator-card-${c.id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="card-soft overflow-hidden"
    >
      <div className={`relative h-28 bg-gradient-to-br ${c.grad}`}>
        <span className="eyebrow absolute left-3 top-3 rounded-full bg-white/90 px-2 py-0.5 text-[0.55rem] text-ink">{t.discover.fictional}</span>
        <span className="eyebrow absolute bottom-3 right-3 rounded-full bg-ink/70 px-2 py-0.5 text-[0.55rem] text-white">{c.tag}</span>
      </div>
      <div className="relative px-5 pb-5">
        <div className={`-mt-8 flex h-16 w-16 items-center justify-center rounded-2xl border-4 border-white bg-gradient-to-br ${c.grad} font-display text-xl font-extrabold text-white shadow-md`}>{initials(c.name)}</div>
        <div className="mt-3 flex items-center gap-1.5">
          <p className="font-display text-lg font-bold text-ink">{c.name}</p>
          <BadgeCheck size={16} className="text-violet-600" />
        </div>
        <p className="text-xs text-mist">{c.handle} · {c.pieces} {t.discover.pieces} · {c.fans} {t.discover.fans}</p>
        <p className="mt-2 text-sm leading-relaxed text-ink-2">{c.bio[lang]}</p>
        <div className="mt-4 grid grid-cols-3 gap-1.5">
          {[0, 1, 2].map((k) => (
            <div key={k} className={`relative flex h-16 items-center justify-center overflow-hidden rounded-lg bg-gradient-to-br ${c.grad} opacity-70`}>
              <div className="absolute inset-0 backdrop-blur-md" />
              <Lock size={14} className="relative text-white" />
            </div>
          ))}
        </div>
        <div className="mt-4 flex gap-2">
          <button disabled data-testid={`creator-follow-${c.id}`} className="pill flex-1 cursor-not-allowed border border-line bg-white py-2 text-xs font-semibold text-mist">{t.discover.follow}</button>
          <button disabled data-testid={`creator-subscribe-${c.id}`} className="pill flex-1 cursor-not-allowed bg-violet-200 py-2 text-xs font-semibold text-violet-700">{t.discover.subscribe} · {t.discover.comingSoon}</button>
        </div>
      </div>
    </motion.article>
  );
};

export default function Discover() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("all");
  const list = CREATORS.filter((c) => (tag === "all" || c.tag === tag) && (c.name + c.handle + c.tag).toLowerCase().includes(q.toLowerCase()));

  return (
    <main data-testid="discover-page" className="mx-auto max-w-7xl px-4 pb-28 pt-28 sm:px-6 lg:px-8">
      <Reveal><Eyebrow>{t.discover.eyebrow}</Eyebrow></Reveal>
      <Reveal delay={0.06}>
        <h1 data-testid="discover-title" className="font-display mt-4 text-4xl font-extrabold tracking-tight text-ink sm:text-5xl lg:text-6xl">{t.discover.title}</h1>
      </Reveal>
      <Reveal delay={0.12}><p className="mt-4 max-w-2xl text-base text-ink-2 sm:text-lg">{t.discover.sub}</p></Reveal>
      <Reveal delay={0.18}>
        <div data-testid="discover-notice" className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{t.discover.notice}</div>
      </Reveal>

      <div className="mt-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <label className="flex w-full items-center gap-2 rounded-full border border-line bg-white px-4 py-2.5 lg:max-w-sm">
          <Search size={16} className="text-mist" />
          <input data-testid="discover-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.discover.search} className="w-full bg-transparent text-sm outline-none placeholder:text-mist" />
        </label>
        <div className="flex flex-wrap gap-1.5">
          {["all", ...CREATOR_TAGS].map((tg) => (
            <button key={tg} data-testid={`discover-filter-${tg.toLowerCase()}`} onClick={() => setTag(tg)} className={`pill px-3 py-1.5 text-xs font-semibold ${tag === tg ? "bg-ink text-white" : "border border-line bg-white text-ink-2 hover:border-violet-300"}`}>
              {tg === "all" ? t.discover.all : tg}
            </button>
          ))}
        </div>
      </div>

      {list.length === 0 ? (
        <p data-testid="discover-empty" className="mt-16 text-center text-ink-2">{t.discover.empty}</p>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {list.map((c, i) => <CreatorCard key={c.id} c={c} i={i} />)}
        </div>
      )}
    </main>
  );
}
